import { Injectable, signal } from "@angular/core";
import { Ref } from "../../../../shared/providers/ref";
import { WarningState } from "./warning-state";
import {
  WarningListEntity,
  WarningItemEntity,
  WarningNarrativeEntity,
  ResponseValueObject,
} from "@gishub/domain";
import { SenamhiRepositoryProvider } from "../../../../shared/providers/repository-providers";
import {
  SenamhiLayerNotifier,
  SenamhiLayerItem,
} from "../senamhi/senamhi-layer-notifier";

@Injectable()
export class WarningNotifier extends SenamhiLayerNotifier<WarningState> {
  private static readonly INITIAL: WarningState = {
    loading: false,
    error: null,
    warnings: null,
    visibleItems: new Set<string>(),
    activeLayerCount: 0,
    layerOpacities: {},
    loadedAt: null,
    emittedAt: null,
    refreshEpoch: 0,
    syncing: false,
    hiddenLevels: [],
  };

  private _loaded = false;

  /** Fetch de lista en vuelo (deduplica precarga + selección explícita). */
  private _loadPromise: Promise<void> | null = null;

  /** Última intención de auto-mostrar el primer aviso al completar la carga. */
  private _autoSelect = true;

  /** Narrativa oficial del aviso seleccionado (scraping SENAMHI). */
  private readonly narrative = signal<WarningNarrativeEntity | null>(null);

  /** Id del aviso cuya narrativa está en vuelo o ya cargada. */
  private narrativeCodeKey: string | null = null;

  constructor(ref: Ref) {
    super(ref, WarningNotifier.INITIAL);
  }

  // ── Hooks de capa SENAMHI ─────────────────────────────────────

  protected getCql(_item: SenamhiLayerItem): string {
    // Igual que la capa 24h de nowcasting: nivel>='Nivel 2' (string).
    return "&CQL_FILTER=nivel%3E%3D%27Nivel%202%27";
  }

  protected resolveItem(key: string): SenamhiLayerItem | null {
    return this.getItems()[Number(key)] ?? null;
  }

  protected buildLayerId(key: string): string {
    return `warning-${key}`;
  }

  protected buildLayerTitle(key: string): string {
    const item = this.getItems()[Number(key)];
    return `Aviso – ${item?.title ?? key}`;
  }

  protected buildFields(): any[] {
    return [
      { name: "OBJECTID", type: "oid" },
      { name: "nivel", type: "string" },
      { name: "codigo", type: "string" },
      { name: "titulo", type: "string" },
      { name: "nombaviso", type: "string" },
      { name: "descripcio", type: "string" },
      { name: "recomendac", type: "string" },
    ];
  }

  // ── Data ──────────────────────────────────────────────────────

  getItems(): WarningItemEntity[] {
    return this.snapshot().warnings?.items ?? [];
  }

  get selectedItem(): WarningItemEntity | null {
    const items = this.getItems();
    const idx = this.getSelectedIndex();
    return idx >= 0 ? (items[idx] ?? null) : null;
  }

  /** Índice del aviso actual (primero visible, o -1 si no hay). */
  get selectedIndex(): number {
    return this.getSelectedIndex();
  }

  /** true si el aviso es ROJO y sigue vigente (estado o fecha de fin). */
  isRed(w: WarningItemEntity): boolean {
    return WarningNotifier.isRedActive(w);
  }

  /** true si el aviso es ROJO y sigue vigente (estado o fecha de fin). */
  private static isRedActive(w: WarningItemEntity): boolean {
    const level = (w.level ?? "").toUpperCase();
    if (!level.includes("ROJO")) return false;
    const status = (w.status ?? "").toLowerCase();
    if (status) return status.includes("vigente");
    const end = new Date(`${w.endDate}T23:59:59`);
    return !Number.isNaN(end.getTime()) && end.getTime() >= Date.now();
  }

  /** Índice del primer ROJO vigente, o -1 si no hay. */
  get redIndex(): number {
    return this.getItems().findIndex((w) => WarningNotifier.isRedActive(w));
  }

  /** Cantidad de ROJOS vigentes. */
  get redCount(): number {
    return this.getItems().filter((w) => WarningNotifier.isRedActive(w)).length;
  }

  /**
   * Índice preferente al entrar a la fuente: el aviso MÁS CRÍTICO
   * (ROJO > NARANJA > AMARILLO), desempatando por vigencia y luego por
   * orden de lista. Antes solo miraba el primer ROJO vigente y, si no
   * había, caía al primero (que podía ser un aviso de menor severidad).
   */
  get preferredIndex(): number {
    const items = this.getItems();
    if (items.length === 0) return 0;
    let best = 0;
    let bestSev = -1;
    let bestVig = -1;
    for (let i = 0; i < items.length; i++) {
      const sev = WarningNotifier.severityRank(items[i]);
      const vig = WarningNotifier.isVigente(items[i]) ? 1 : 0;
      if (sev > bestSev || (sev === bestSev && vig > bestVig)) {
        best = i;
        bestSev = sev;
        bestVig = vig;
      }
    }
    return best;
  }

  /** Rango de severidad del aviso (ROJO=4 · NARANJA=3 · AMARILLO=2 · otro=1). */
  private static severityRank(w: WarningItemEntity): number {
    const level = (w.level ?? "").toUpperCase();
    if (level.includes("ROJO")) return 4;
    if (level.includes("NARANJA")) return 3;
    if (level.includes("AMARILLO")) return 2;
    return 1;
  }

  /** Vigencia por estado o por fecha de fin (aviso no-rojo incluido). */
  private static isVigente(w: WarningItemEntity): boolean {
    const status = (w.status ?? "").toLowerCase();
    if (status) return status.includes("vigente");
    const end = new Date(`${w.endDate}T23:59:59`);
    return !Number.isNaN(end.getTime()) && end.getTime() >= Date.now();
  }

  /** Índice del primer aviso visible (si hay varios, el primero). */
  private getSelectedIndex(): number {
    const s = this.snapshot();
    if (s.activeLayerCount === 0) return -1;
    const items = this.getItems();
    for (let i = 0; i < items.length; i++) {
      if (s.visibleItems.has(String(i))) return i;
    }
    return -1;
  }

  async loadWarnings(autoSelect = true): Promise<void> {
    // La última llamada define la intención de auto-selección: así una
    // precarga en vuelo no agrega capas al mapa si el usuario aún está
    // en otra fuente (y sí las agrega si ya pidió "avisos").
    this._autoSelect = autoSelect;

    // Un solo fetch en vuelo: quien llegue tarde (p. ej. "Atender"
    // durante la precarga) espera la MISMA promesa y luego auto-selecciona.
    if (!this._loaded) {
      this._loaded = true;
      this._loadPromise = this.fetchWarnings();
    }
    if (this._loadPromise) {
      try {
        await this._loadPromise;
      } finally {
        this._loadPromise = null;
      }
    }
    if (this._autoSelect) await this.autoAddMostRecentLayer();
  }

  /** Fetch único de la lista de avisos (sin auto-selección). */
  private async fetchWarnings(): Promise<void> {
    this.updateState((s) => ({ ...s, loading: true, error: null }));
    this._lastSyncAt = Date.now();
    try {
      const repo = this.ref.watch(SenamhiRepositoryProvider).instance;
      const res: ResponseValueObject<WarningListEntity> =
        await repo.getWarningsList();
      if (res.success && res.data) {
        const items = res.data!.items ?? [];
        const lastEmission = items
          .map((i) => Date.parse(i.emissionDate))
          .filter((t) => !Number.isNaN(t))
          .sort((a, b) => b - a)[0];
        this.updateState((s) => ({
          ...s,
          warnings: res.data!,
          loading: false,
          syncing: false,
          loadedAt: Date.now(),
          emittedAt:
            typeof lastEmission === "number" && !Number.isNaN(lastEmission)
              ? new Date(lastEmission).toISOString()
              : s.emittedAt,
        }));
      } else {
        this.updateState((s) => ({
          ...s,
          error: res.message ?? "Sin datos",
          loading: false,
          syncing: false,
        }));
      }
    } catch (err: any) {
      this.updateState((s) => ({
        ...s,
        error: err?.message ?? "Error al cargar avisos SENAMHI",
        loading: false,
        syncing: false,
      }));
    }
  }

  // ── Auto-refresh silencioso (R1) ─────────────────────────────

  protected shouldAutoRefresh(): boolean {
    // La lista de avisos alimenta la campana de emergencia y el conteo de
    // rojos vigentes: se refresca en segundo plano una vez cargada.
    return this._loaded;
  }

  protected async autoRefresh(): Promise<void> {
    this.updateState((s) => ({ ...s, syncing: true }));
    this._lastSyncAt = Date.now();
    try {
      const repo = this.ref.watch(SenamhiRepositoryProvider).instance;
      const res: ResponseValueObject<WarningListEntity> =
        await repo.getWarningsList();
      if (res.success && res.data) {
        const items = res.data!.items ?? [];
        const lastEmission = items
          .map((i) => Date.parse(i.emissionDate))
          .filter((t) => !Number.isNaN(t))
          .sort((a, b) => b - a)[0];
        this.updateState((s) => ({
          ...s,
          warnings: res.data!,
          loadedAt: Date.now(),
          emittedAt:
            typeof lastEmission === "number" && !Number.isNaN(lastEmission)
              ? new Date(lastEmission).toISOString()
              : s.emittedAt,
        }));
        // Selección viva: si el aviso visible ya no existe, se baja al
        // preferente (rojo vigente primero); la narrativa se re-ancla.
        await this.ensureFreshSelection();
        await this.syncLayersAndIncrementEpoch();
      } else {
        // Silencioso: conservar la lista previa y reintentar en el tick.
        this.updateState((s) => ({ ...s, syncing: false }));
      }
    } catch {
      this.updateState((s) => ({ ...s, syncing: false }));
    }
  }

  /** Asegura que la selección visible siga resolviendo tras re-emitir. */
  private async ensureFreshSelection(): Promise<void> {
    const items = this.getItems();
    if (items.length === 0) return;
    const visible = [...this.snapshot().visibleItems];
    const valid = visible.filter((k) => this.resolveItem(k) != null);
    if (valid.length > 0) {
      const idx = Number(valid[0]);
      if (idx >= 0) void this.ensureNarrative(idx);
      return;
    }
    const key = String(this.preferredIndex);
    await this.selectLayer(key);
    void this.ensureNarrative(Number(key));
  }

  // ── Item layer select (radio: one at a time) ──────────────────

  async selectWarning(index: number): Promise<boolean> {
    // La narrativa corre en paralelo; el renderizado del polígono en el mapa no espera el scraping web
    void this.ensureNarrative(index);
    return this.selectLayer(String(index));
  }

  /**
   * Carga (bajo demanda) la narrativa oficial del aviso `index` desde
   * la página de detalle SENAMHI. Silencioso: sin narrativa, el panel
   * cae al respaldo WFS. Idempotente por aviso.
   */
  async ensureNarrative(index: number): Promise<void> {
    const item = this.getItems()[index];
    if (!item) return;

    const key = item.codeKey ?? String(index);
    if (this.narrativeCodeKey === key) return;
    this.narrativeCodeKey = key;
    this.narrative.set(null);

    try {
      const repo = this.ref.watch(SenamhiRepositoryProvider).instance;
      const res = await repo.getWarningNarrative(
        item.code,
        item.nroMapa,
        item.year,
      );
      if (this.narrativeCodeKey === key && res.success && res.data) {
        this.narrative.set(res.data);
      }
    } catch {
      // Degradación elegante: el respaldo WFS cubre la UI.
    }
  }

  isWarningVisible(index: number): boolean {
    return this.isLayerVisible(String(index));
  }

  getItemOpacity(index: number): number {
    return this.getLayerOpacity(String(index));
  }

  setLayerOpacity(index: number, opacity: number): void {
    this.applyLayerOpacity(String(index), opacity);
  }

  // ── Auto-add most recent (primer aviso) ──────────────────────

  private async autoAddMostRecentLayer(): Promise<void> {
    const items = this.getItems();
    if (items.length === 0) return;
    // Prioridad de severidad: primero el ROJO vigente; si no hay, el primero.
    const key = String(this.preferredIndex);
    if (!this.snapshot().visibleItems.has(key)) {
      // La narrativa viaja en paralelo: el polígono no espera el scraping.
      void this.ensureNarrative(Number(key));
      await this.selectLayer(key);
    }
  }

  formatDate(iso: string): string {
    try {
      const d = new Date(iso);
      const months = [
        "ene",
        "feb",
        "mar",
        "abr",
        "may",
        "jun",
        "jul",
        "ago",
        "sep",
        "oct",
        "nov",
        "dic",
      ];
      return `${String(d.getDate()).padStart(2, "0")} ${months[d.getMonth()]} ${d.getFullYear()}`;
    } catch {
      return iso;
    }
  }

  /**
   * Descripción oficial del aviso activo. Fuente principal: la página
   * de detalle SENAMHI (scraping, párrafos por día); respaldo: atributos
   * WFS de los polígonos (descripcio / recomendac).
   */
  get selectedDetail(): { description: string; zones: string } | null {
    const idx = this.getSelectedIndex();
    if (idx < 0) return null;

    const item = this.getItems()[idx] ?? null;
    if (!item) return null;

    const narrative = this.narrative();
    const attrs = this.getLayerAttributes(String(idx));

    const pick = (a: Record<string, unknown>, ...keys: string[]): string => {
      for (const k of keys) {
        const v = a[k];
        if (typeof v === "string" && v.trim()) return v.trim();
      }
      return "";
    };

    // Respaldo WFS: zonas declaradas por SENAMHI en los polígonos.
    const wfsZones = attrs
      ? [
          ...new Set(
            attrs
              .map((a) => pick(a, "nombaviso", "nomb", "distrito"))
              .filter(Boolean),
          ),
        ].join(" · ")
      : "";

    const description =
      narrative?.paragraphs.join(" ") ??
      (attrs ? pick(attrs[0], "descripcio", "recomendac") : "");

    if (!description && !wfsZones) return null;
    return { description, zones: wfsZones };
  }
}
